import React, { useMemo } from 'react';
import { StyleSheet, Pressable, View, Platform } from 'react-native';
import { BlurView } from 'expo-blur';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { getShadowThemeStyles, isDarkTheme } from '@/constants/Colors';
import { BORDER_RADIUS } from '@/constants/config';

const createStyles = (colors, theme, themeStyles, isXS, insetBottom, wrapActions) => StyleSheet.create({
  floatingBarWrapper: {
    position: 'absolute',
    bottom: (isXS ? 16 : 20) + insetBottom,
    ...(isXS
      ? { left: 0, right: 0, marginHorizontal: 16, width: undefined }
      : {
          left: '50%',
          ...(Platform.OS === 'web' ? { transform: 'translateX(-50%)' } : { transform: [{ translateX: -150 }] }),
        }),
    borderRadius: isXS ? BORDER_RADIUS.medium : BORDER_RADIUS.round,
    borderWidth: 1,
    borderColor: themeStyles.borderColor,
    overflow: 'hidden',
    minWidth: isXS ? undefined : 300,
    maxWidth: isXS ? undefined : 620,
    zIndex: 100,
    ...(Platform.OS === 'web' ? {
      boxShadow: isDarkTheme(theme) ? themeStyles.boxShadow : 'none',
    } : {
      shadowColor: themeStyles.shadowColor,
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: isDarkTheme(theme) ? 0.3 : 0.1,
      shadowRadius: 8,
      elevation: 4,
    }),
  },
  blurLayer: {
    ...StyleSheet.absoluteFillObject,
    ...(Platform.OS === 'web' && {
      backdropFilter: 'blur(18px) saturate(1.8)',
      WebkitBackdropFilter: 'blur(18px) saturate(1.8)',
    }),
  },
  floatingBar: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    flexDirection: isXS ? 'column' : 'row',
    alignItems: 'center',
    justifyContent: isXS ? 'center' : 'space-between',
    gap: isXS ? 10 : 12,
  },
  headerRow: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  clearButton: {
    padding: 8,
    borderRadius: isXS ? BORDER_RADIUS.medium : BORDER_RADIUS.round,
  },
  clearButtonHovered: {
    backgroundColor: colors.solid_color_tertiary,
    opacity: 0.8,
  },
  floatingBarText: {
    flex: isXS ? 0 : 1,
    textAlign: isXS ? 'right' : 'left',
  },
  actionsContainer: {
    flexDirection: 'row',
    alignItems: 'stretch',
    justifyContent: isXS ? 'stretch' : 'center',
    gap: 8,
    width: isXS ? '100%' : undefined,
    flexWrap: wrapActions ? 'wrap' : 'nowrap',
  },
  floatingBarButton: {
    backgroundColor: colors.accent_text_color,
    paddingHorizontal: wrapActions ? 10 : 16,
    paddingVertical: 8,
    borderRadius: isXS ? BORDER_RADIUS.medium : BORDER_RADIUS.round,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    ...(isXS && (wrapActions
      ? { flexGrow: 1, flexBasis: '47%' }
      : { flex: 1 })),
  },
  floatingBarButtonHovered: {
    opacity: 0.9,
  },
  floatingBarButtonDanger: {
    backgroundColor: colors.color_destructive,
  },
});

/**
 * FloatingSelectButton - A floating action bar for multi-select operations
 * 
 * @param {string} title - Text showing selection count
 * @param {string} actionText - Primary action button text (legacy single-action mode)
 * @param {function} onAction - Primary action handler (legacy single-action mode)
 * @param {Array} actions - Array of action objects: { text, icon, onPress, variant: 'primary'|'secondary'|'danger' }
 * @param {function} onClear - Handler to clear selection
 */
const FloatingSelectButton = ({ title, actionText, onAction, actions, onClear }) => {
  const { colors, theme } = useTheme();
  const { isXS } = useResponsiveness();
  const insets = useSafeAreaInsets();
  const themeStyles = useMemo(() => getShadowThemeStyles(theme), [theme]);

  // Support legacy single-action mode
  const actionsList = actions || (actionText && onAction ? [{ text: actionText, onPress: onAction, variant: 'primary' }] : []);
  const wrapActions = isXS && actionsList.length > 3;
  const styles = useMemo(
    () => createStyles(colors, theme, themeStyles, isXS, insets.bottom, wrapActions),
    [colors, theme, themeStyles, isXS, insets.bottom, wrapActions],
  );

  const isPrimaryOrSecondary = (v) => v === 'primary' || v === 'secondary';

  const clearButton = onClear ? (
    <Pressable
      style={({ hovered }) => [styles.clearButton, hovered && styles.clearButtonHovered]}
      accessibilityRole="button"
      accessibilityLabel="Clear selection"
      onPress={onClear}
    >
      <Icon name="xmark" size={16} color={colors.text_color_primary} />
    </Pressable>
  ) : null;

  const blurTint = isDarkTheme(theme) ? 'dark' : 'light';
  const blurBgStyle = Platform.OS === 'web'
    ? { backgroundColor: isDarkTheme(theme) ? 'rgba(16,16,16,0.5)' : 'rgba(255,255,255,0.5)' }
    : {};

  return (
    <View style={styles.floatingBarWrapper}>
      <BlurView
        intensity={80}
        tint={blurTint}
        style={[styles.blurLayer, blurBgStyle]}
      />
      <View style={styles.floatingBar} pointerEvents="box-none">
      {isXS ? (
        <>
          <View style={styles.headerRow}>
            {clearButton ?? <View style={{ width: 34 }} />}
            <Text style={styles.floatingBarText} color="primary" weight="medium">
              {title}
            </Text>
          </View>
          <View style={styles.actionsContainer}>
            {actionsList.map((action, index) => (
              <Pressable
                key={index}
                style={({ hovered }) => [
                  styles.floatingBarButton,
                  action.variant === 'danger' && styles.floatingBarButtonDanger,
                  hovered && styles.floatingBarButtonHovered,
                ]}
                accessibilityRole="button"
                onPress={action.onPress}
                accessibilityLabel={action.accessibilityLabel || action.text}
              >
                {action.icon && (
                  <Icon
                    name={action.icon}
                    size={14}
                    color={isPrimaryOrSecondary(action.variant) ? colors.accent_color_primary : '#fff'}
                  />
                )}
                <Text weight="bold" style={{ color: isPrimaryOrSecondary(action.variant) ? colors.accent_color_primary : '#fff' }}>
                  {action.text}
                </Text>
              </Pressable>
            ))}
          </View>
        </>
      ) : (
        <>
          {clearButton}
          <Text style={styles.floatingBarText} color="primary" weight="medium">
            {title}
          </Text>
          <View style={styles.actionsContainer}>
            {actionsList.map((action, index) => (
              <Pressable
                key={index}
                style={({ hovered }) => [
                  styles.floatingBarButton,
                  action.variant === 'danger' && styles.floatingBarButtonDanger,
                  hovered && styles.floatingBarButtonHovered,
                ]}
                accessibilityRole="button"
                onPress={action.onPress}
                accessibilityLabel={action.accessibilityLabel || action.text}
              >
                {action.icon && (
                  <Icon
                    name={action.icon}
                    size={14}
                    color={isPrimaryOrSecondary(action.variant) ? colors.accent_color_primary : '#fff'}
                  />
                )}
                <Text weight="bold" style={{ color: isPrimaryOrSecondary(action.variant) ? colors.accent_color_primary : '#fff' }}>
                  {action.text}
                </Text>
              </Pressable>
            ))}
          </View>
        </>
      )}
      </View>
    </View>
  );
};

export default React.memo(FloatingSelectButton);